/**
 * 同時実行数を制限しながら非同期処理を並列実行するユーティリティ。
 *
 * 大量のリクエストを `Promise.all` で一気に投げるとブラウザの同時接続数や
 * サーバー側の負荷で不安定になるため、上限を決めて流し込む。
 */

/**
 * `items` を `worker` に流し、同時実行数を `concurrency` 件までに制限する。
 * 戻り値は入力と同じ順序になる。
 *
 * `worker` が投げた例外はそのまま伝播する（呼び出し側で握り潰す前提の場合は
 * `worker` 内で try/catch すること）。
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  if (items.length === 0) return [];

  const limit = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const runners = Array.from({ length: limit }, async () => {
    for (;;) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  });

  await Promise.all(runners);
  return results;
};
